<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown } from '@lucide/vue'

const props = withDefaults(
  defineProps<{
    pagination: {
      total: number
      pageSize: number
      pageNumber: number
      pageTotal: number
    }
  }>(),
  {
    pagination: () => ({ total: 0, pageSize: 0, pageNumber: 0, pageTotal: 0 }),
  },
)

const localPageSize = ref(props.pagination.pageSize)

const emit = defineEmits<{ paginig: [pageSize: number, pageNumber: number] }>()

const previousPage = () => {
  const allPagesCount = Math.ceil(props.pagination.total / localPageSize.value)

  if (allPagesCount < props.pagination.pageNumber - 1) {
    emit('paginig', localPageSize.value, 1)
    return
  }

  if (localPageSize.value > props.pagination.total) {
    emit('paginig', localPageSize.value, 1)
    return
  }

  emit(
    'paginig',
    localPageSize.value,
    props.pagination.pageNumber - 1 > 0 ? props.pagination.pageNumber - 1 : 1,
  )
}

const nextPage = () => {
  const allPagesCount = Math.ceil(props.pagination.total / localPageSize.value)

  if (allPagesCount < props.pagination.pageNumber - 1) {
    emit('paginig', localPageSize.value, 1)
    return
  }

  if (localPageSize.value > props.pagination.total) {
    emit('paginig', localPageSize.value, 1)
    return
  }

  emit(
    'paginig',
    localPageSize.value,
    props.pagination.pageNumber < props.pagination.pageTotal
      ? props.pagination.pageNumber + 1
      : props.pagination.pageTotal,
  )
}

const firstPage = () => {
  emit('paginig', localPageSize.value, 1)
}
const lastPage = () => {
  emit('paginig', localPageSize.value, props.pagination.pageTotal || 1)
}

const pageSizeOptions = [10, 20, 50, 100]
const sizeDropOpen = ref(false)
const sizeDropRef = ref<HTMLElement | null>(null)

const selectPageSize = (n: number) => {
  localPageSize.value = n
  sizeDropOpen.value = false
  emit('paginig', n, 1)
}

const handleClickOutside = (e: MouseEvent) => {
  if (sizeDropRef.value && !sizeDropRef.value.contains(e.target as Node)) {
    sizeDropOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))
</script>

<template>
    <div class="flex items-center flex-wrap gap-4">
      <p class="text-xs text-muted-foreground dark:text-gray-400">
        {{ $t('common.list') }}:
        <span class="font-semibold text-foreground dark:text-white/90">
          {{
            pagination.pageSize * pagination.pageNumber > pagination.total
              ? pagination.total
              : pagination.pageSize * pagination.pageNumber
          }}
        </span>
        {{ $t('common.of') }}
        <span class="font-semibold text-foreground dark:text-white/90">{{ pagination.total }}</span>
      </p>

      <div class="flex items-center gap-2 ml-auto">
        <div class="flex items-center gap-1">
          <button
            @click="firstPage"
            :disabled="pagination.pageNumber === 1"
            class="bg-card w-8 h-8 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:text-white/90 dark:hover:bg-gray-800"
          >
            <ChevronsLeft :size="14" />
          </button>

          <button
            @click="previousPage"
            :disabled="pagination.pageNumber === 1"
            class="bg-card w-8 h-8 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:text-white/90 dark:hover:bg-gray-800"
          >
            <ChevronLeft :size="14" />
          </button>

          <div class="relative flex items-center" ref="sizeDropRef">
            <button
              @click="sizeDropOpen = !sizeDropOpen"
              class="bg-card h-8 px-2.5 rounded-lg border border-border text-xs font-semibold text-foreground hover:bg-muted transition-all flex items-center gap-1 cursor-pointer dark:bg-gray-900 dark:border-gray-700 dark:text-white/90 dark:hover:bg-gray-800"
            >
              {{ localPageSize }}
              <ChevronDown
                :size="12"
                class="text-muted-foreground transition-transform dark:text-gray-400"
                :class="sizeDropOpen ? 'rotate-180' : ''"
              />
            </button>

            <div
              v-if="sizeDropOpen"
              class="absolute top-full right-0 z-10 mt-1 w-16 rounded-lg border border-border bg-card shadow-lg overflow-hidden z-11 dark:bg-gray-900 dark:border-gray-700"
            >
              <button
                v-for="opt in pageSizeOptions"
                :key="opt"
                @click="selectPageSize(opt)"
                class="w-full text-left px-2.5 py-1.5 text-xs font-semibold transition-all cursor-pointer"
                :class="
                  localPageSize === opt
                    ? 'bg-primary text-white'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted dark:text-gray-400 dark:hover:text-white/90 dark:hover:bg-gray-800'
                "
              >
                {{ opt }}
              </button>
            </div>
          </div>

          <button
            @click="nextPage"
            :disabled="pagination.pageNumber === pagination.pageTotal"
            class="bg-card w-8 h-8 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:text-white/90 dark:hover:bg-gray-800"
          >
            <ChevronRight :size="14" />
          </button>

          <button
            @click="lastPage"
            :disabled="pagination.pageNumber === pagination.pageTotal"
            class="bg-card w-8 h-8 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:text-white/90 dark:hover:bg-gray-800"
          >
            <ChevronsRight :size="14" />
          </button>
        </div>
      </div>
    </div>
</template>
